import {
  Avatar,
  Box,
  Button,
  Flex,
  IconButton,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Text,
  useDisclosure,
} from '@chakra-ui/react';
import { ContactAddToType } from '@texas/api/endpoints/contactsApi';
import { inquiryApi, SupplierInquiry } from '@texas/api/endpoints/inquiryApi';
import { UserType } from '@texas/api/endpoints/userApi';
import { useApiRequest } from '@texas/api/hooks/useApiRequest';
import { SelectContact } from '@texas/components/contacts/SelectContact';
import { SelectUserModal } from '@texas/components/customer/users/SelectUserModal';
import { Icons } from '@texas/components/shared/Icons';
import { WarningComponent } from '@texas/components/shared/WarningComponent';
import { useTranslation } from 'react-i18next';
import { inquiryEvents } from '../events';

interface Props {
  data: SupplierInquiry;
}

interface PersonRowProps {
  label: string;
  name: string | null;
  detail?: string | null;
  warning?: string;
  onEdit: () => void;
  onRemove: () => void;
  removing: boolean;
}

export function InquiryContactsSection({ data }: Props) {
  const { t } = useTranslation();
  const {
    isOpen: isContactOpen,
    onOpen: onContactOpen,
    onClose: onContactClose,
  } = useDisclosure();
  const {
    isOpen: isOwnerOpen,
    onOpen: onOwnerOpen,
    onClose: onOwnerClose,
  } = useDisclosure();
  const {
    request: updateSupplierContactRequest,
    loading: updatingSupplierContact,
  } = useApiRequest(inquiryApi.updateSupplierContact);
  const { request: updateInternalOwnerRequest, loading: updatingOwner } =
    useApiRequest(inquiryApi.updateInternalOwner);

  return (
    <Box
      borderTopWidth={{ base: '1px', lg: 0 }}
      borderLeftWidth={{ base: 0, lg: '1px' }}
      borderStyle="solid"
      borderColor="inquiryOverview.softBorder"
      px={{ base: 0, lg: 6, '2xl': 8 }}
      py={{ base: 5, lg: 0 }}
    >
      <Text fontSize="xs" fontWeight="bold" textTransform="uppercase" mb={1}>
        {t('inquiry.contactsAndOwnership')}
      </Text>
      <PersonRow
        label={t('suppliers.supplierContact')}
        name={data.supplierContactName}
        detail={data.supplierContactEmail}
        warning={
          data.supplierContactId == null
            ? t('inquiry.assignSupplierContactWarning')
            : undefined
        }
        onEdit={onContactOpen}
        removing={updatingSupplierContact}
        onRemove={async () => {
          const res = await updateSupplierContactRequest(data.id, {
            supplierContactId: null,
          });
          if (!res.hasError) inquiryEvents.onUpdated.dispatch();
        }}
      />
      <Box borderTop="1px solid" borderColor="inquiryOverview.softBorder" />
      <PersonRow
        label={t('inquiry.internalOwner')}
        name={data.internalOwnerName}
        warning={
          data.internalOwnerId == null
            ? t('inquiry.assignInternalOwnerWarning')
            : undefined
        }
        onEdit={onOwnerOpen}
        removing={updatingOwner}
        onRemove={async () => {
          const res = await updateInternalOwnerRequest(data.id, {
            internalOwnerId: null,
          });
          if (!res.hasError) inquiryEvents.onUpdated.dispatch();
        }}
      />
      <Modal isOpen={isContactOpen} onClose={onContactClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{t('suppliers.supplierContact')}</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            {data.supplierId != null && (
              <SelectContact
                referenceId={data.supplierId}
                mode="select"
                filterOn={ContactAddToType.Supplier}
                // eslint-disable-next-line @typescript-eslint/no-empty-function
                onCreateContactClick={() => {}}
                onSelect={async (contact) => {
                  const res = await updateSupplierContactRequest(data.id, {
                    supplierContactId: contact.id,
                  });
                  if (!res.hasError) {
                    onContactClose();
                    inquiryEvents.onUpdated.dispatch();
                  }
                }}
              />
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
      <SelectUserModal
        customerId={data.customerId}
        branchId={data.branchId}
        userType={UserType.ProductDeveloper}
        filterIds={data.internalOwnerId != null ? [data.internalOwnerId] : []}
        isOpen={isOwnerOpen}
        onClose={onOwnerClose}
        onSelect={async (id) => {
          const res = await updateInternalOwnerRequest(data.id, {
            internalOwnerId: id,
          });
          if (!res.hasError) {
            onOwnerClose();
            inquiryEvents.onUpdated.dispatch();
          }
        }}
      />
    </Box>
  );
}

function PersonRow({
  label,
  name,
  detail,
  warning,
  onEdit,
  onRemove,
  removing,
}: PersonRowProps) {
  const { t } = useTranslation();

  return (
    <Flex align="center" gap={3} py={3} minW={0}>
      {name ? (
        <Avatar
          name={name}
          size="sm"
          bg="inquiryOverview.badgeBackground"
          color="inquiryOverview.text"
        />
      ) : (
        <Box
          boxSize="40px"
          border="2px dashed"
          borderColor="inquiryOverview.softBorder"
          borderRadius="full"
          flexShrink={0}
        />
      )}
      <Box minW={0} flex={1}>
        <WarningComponent warning={warning} isMandatory={true}>
          <Text
            fontSize="sm"
            fontWeight="semibold"
            color="inquiryOverview.text"
          >
            {label}
          </Text>
        </WarningComponent>
        <Flex align="center" gap={2} wrap="wrap">
          <Text fontSize="sm" color="inquiryOverview.mutedText" noOfLines={1}>
            {name ?? t('general.notAssigned')}
          </Text>
          <Button size="xs" variant="link" color="blue.500" onClick={onEdit}>
            {name ? t('general.edit') : t('inquiry.assign')}
          </Button>
          {name && (
            <IconButton
              aria-label={t('general.remove')}
              icon={<Icons.Close boxSize={3} />}
              size="xs"
              variant="ghost"
              isLoading={removing}
              onClick={onRemove}
            />
          )}
        </Flex>
        {detail && (
          <Text fontSize="xs" color="inquiryOverview.mutedText" noOfLines={1}>
            {detail}
          </Text>
        )}
      </Box>
    </Flex>
  );
}
